import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { APPLY_PATH, IMAGES } from "@/data/content";

const LINES = ["Bring the Jet Car", "Experience to Your", "Waterfront Business"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const scrollToProgram = () => {
    const el = document.getElementById("program");
    if (el) window.__lenis?.scrollTo(el, { offset: -80, duration: 1.4 });
  };

  return (
    <section ref={ref} className="noise relative flex min-h-[100svh] items-end overflow-hidden bg-ink" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-24">
        <img
          src={IMAGES.hero}
          alt="Red Jet Car throwing spray across the water in front of the Miami waterfront skyline"
          className="h-full w-full scale-105 object-cover"
          fetchpriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/85 via-ink/50 to-ink" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/35 to-transparent" />
      <div className="spotlight absolute inset-0" />

      <motion.div style={{ opacity: fade }} className="container-x relative z-10 pb-24 pt-40 sm:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="eyebrow mb-6"
          data-testid="hero-eyebrow"
        >
          Xtreme Exotics Watersports LLC · Fleet Partner Program
        </motion.p>

        <h1 className="font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-offwhite sm:text-5xl lg:text-6xl" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                {i === 1 ? (
                  <>
                    Experience <span className="text-aqua">to Your</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.8 }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-muted sm:text-lg"
          data-testid="hero-subcopy"
        >
          Add a premium attraction to your existing rental operation without purchasing the Jet Car. Xtreme Exotics
          provides the fleet, maintenance, training and marketing support. Qualified Fleet Partners provide the
          waterfront location, operating staff and local customer experience.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link to={APPLY_PATH} className="btn-primary" data-testid="hero-apply-btn">
            Apply to Become a Fleet Partner <ArrowRight size={16} />
          </Link>
          <button onClick={scrollToProgram} className="btn-ghost" data-testid="hero-how-btn">
            See How the Program Works
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 max-w-xl text-xs leading-relaxed tracking-wide text-muted/80"
          data-testid="hero-qualification-line"
        >
          Designed for established Jet Ski rentals, pontoon rentals, watersports operators, marinas and waterfront
          resorts.
        </motion.p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 text-aqua/70 sm:block"
        aria-hidden="true"
      >
        <ChevronDown size={22} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
